import * as React from 'react';
import ReactTooltip from 'react-tooltip';
import clsx from 'clsx';
import {
  IBFilterSelectProps,
  IBFilterSelectState,
  ICancellationToken
} from './FilterSelect.types';
import {
  handleOnOptionClick,
  getData,
  handleOnBlur,
  getPromiseWithCancel
} from './FilterSelect.logic';
import FilterSelectOption from './FilterSelectOption';
// @ts-ignore
import selectArrow from '../../assets/icons/select-arrow.svg';
import { prepareOptions } from '../Select/Select.logic';
import {
  FilterSelectList,
  SelectArrowImg,
  SelectInput
} from '../Select/Select';
import { StyledDropdown } from '../Select/DropdownComponents';

const FilterSelect: React.FC<IBFilterSelectProps> = (
  props: IBFilterSelectProps
) => {
  const {
    id,
    values,
    onSelect,
    minSearchTextLength,
    searchTimeout = 150,
    className,
    value,
    clientSideFilter,
    useGroups,
    placeholder,
    styleType,
    testId,
    defaultOptions = [],
    maxDropdownHeight
  } = props;
  const [state, setState] = React.useState<IBFilterSelectState>({
    options: defaultOptions,
    selectedOption: { value: '', label: '' },
    newSearchText: '',
    searchText: ''
  });

  const inputElement = React.useRef<HTMLInputElement>();
  const optionsDomRefreshTimeout = 100;
  const [, forceRefresh] = React.useState({});
  const forceRerender = () =>
    setTimeout(() => forceRefresh({}), optionsDomRefreshTimeout);

  const cancellationToken: ICancellationToken = { cancel: () => {} };

  React.useEffect(() => {
    const searchFunction = setTimeout(() => {
      getData(
        state,
        setState,
        minSearchTextLength,
        filterText =>
          getPromiseWithCancel(values(filterText), cancellationToken),
        clientSideFilter,
        useGroups,
        defaultOptions
      );
      forceRerender();
    }, searchTimeout);
    return () => clearTimeout(searchFunction);
  }, [state]);

  React.useEffect(() => {
    if (state.selectedOption !== value) {
      setState({
        ...state,
        selectedOption: value,
        searchText: value ? value.label : ''
      });
    }
  }, [value]);

  const onSelectInpupClickHandler = () => {
    if (inputElement) {
      setState({
        ...state,
        searchText: inputElement.current.value,
        allowShow: !state.allowShow
      });
      forceRerender();
    }
  };

  const onSelectArrowClickHandler = () => {
    inputElement.current.focus();
    setState({
      ...state,
      options: defaultOptions,
      inFocus: true,
      allowShow: true
    });
    forceRerender();
  };

  const onSelectInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    cancellationToken.cancel();
    if (event.target) {
      setState({
        ...state,
        searchText: event.target.value,
        allowShow: state.allowShow || (state.options && !!state.options.length)
      });
    }
    forceRerender();
  };

  return (
    <div className={className}>
      <FilterSelectList>
        <SelectInput
          ref={inputElement}
          placeholder={placeholder}
          className={clsx({
            input: !styleType || styleType === 'standard',
            'input-inline': styleType && styleType === 'inline',
            'w-100p': true
          })}
          onBlur={() => handleOnBlur(state, setState)}
          onFocus={event => {
            setState({
              ...state,
              searchText: event.target.value,
              inFocus: true
            });
            forceRerender();
          }}
          onClick={() => onSelectInpupClickHandler()}
          id={id}
          value={state.searchText ?? null}
          onChange={e => onSelectInputChange(e)}
          data-testid={testId || 'filter-select-input'}
        />
        <SelectArrowImg
          className="select--select-arrow"
          src={selectArrow}
          alt="Select arrow"
          onClick={() => onSelectArrowClickHandler()}
          style={{ cursor: 'pointer' }}
        />
        {state.allowShow && state.options && state.inFocus ? (
          <StyledDropdown
            className="w-100"
            maxDropdownHeight={maxDropdownHeight}
          >
            {prepareOptions(state.options, useGroups).map(option => (
              <FilterSelectOption
                key={option.value}
                option={option}
                onClick={e =>
                  handleOnOptionClick(e, option, state, setState, onSelect)
                }
                onMouseEnter={() => ReactTooltip.rebuild()}
                useGroups={useGroups}
              />
            ))}
          </StyledDropdown>
        ) : null}
      </FilterSelectList>
      <ReactTooltip
        id="FilterSelectTooltip"
        effect="solid"
        place="right"
        multiline
      />
    </div>
  );
};

export default FilterSelect;
